import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        
        // Reset success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-blue-500 font-bold text-sm uppercase tracking-wider bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
              Get in Touch
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
            Contact <span className="text-blue-500">Us</span>
          </h2>
          <p className="text-xl text-slate-600">
            Ready to transform your digital presence? Let's start a conversation.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-3xl font-black text-slate-900 mb-8">Contact Information</h3>
            
            <div className="space-y-6">
              {/* Email */}
              <div className="bg-slate-800 border-2 border-slate-700 hover:border-blue-400 transition-all duration-300 hover:shadow-lg rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Email Us</h4>
                    <a 
                      href={`mailto:${companyInfo.email}`}
                      className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-slate-800 border-2 border-slate-700 hover:border-blue-400 transition-all duration-300 hover:shadow-lg rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Call Us</h4>
                    <a 
                      href={`tel:${companyInfo.phone}`}
                      className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-slate-800 border-2 border-slate-700 hover:border-blue-400 transition-all duration-300 hover:shadow-lg rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Visit Us</h4>
                    <p className="text-slate-300 font-semibold">
                      Australia
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Contact Us */}
            <div className="mt-8 p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl text-white">
              <h4 className="text-2xl font-black mb-4">Why Contact Us?</h4>
              <p className="text-slate-300 leading-relaxed">
                Our team is here to help! Contact us for personalized answers and expert guidance.
                We respond to all inquiries within 24 hours.
              </p>
              <Button
                onClick={() => window.location.href = `mailto:${companyInfo.email}`}
                className="mt-6 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-bold transition-all duration-300"
              >
                Get in Touch
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-white border-2 border-blue-200 shadow-xl rounded-xl p-8">
              <h3 className="text-2xl font-black text-slate-900 mb-6">Send us a Message</h3>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <p className="text-green-700 font-semibold">Message sent successfully! We'll get back to you soon.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center space-x-3">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                    <p className="text-red-700 font-semibold">Failed to send message. Please try again.</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-slate-700 font-bold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-slate-700 font-bold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-slate-700 font-bold mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                      placeholder="0415365783"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-slate-700 font-bold mb-2">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-4 rounded-xl font-black text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };
  
  export default Contact;
